import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useGlobalSnackbar } from "src/context/SnackbarContext";
import { invalidateContacts } from "src/utils";
import { invalidateContactResolutionQueries } from "../../virtual-receptionist/contacts/hooks/useContacts";
import { createVendor as createVendorApi } from "../actions/createVendors.actions";
import { deleteVendor as deleteVendorApi } from "../actions/deleteVendors.actions";
import { getVendorByUidApi, getVendors } from "../actions/getVendors.actions";
import { updateVendor as updateVendorApi } from "../actions/updateVendors.actions";
import { useVendorStore } from "../store/useVendorStore";

interface GetVendorsParams {
  page?: number;
  pageSize?: number;
  orderBy?: string;
  orderField?: string;
  search?: string;
  filterBy?: string;
  status?: string;
}

export const useVendors = () => {
  const token = sessionStorage.getItem("token");
  const queryClient = useQueryClient();
  const { showSnackbar } = useGlobalSnackbar();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { setVendorsPaginated, addVendor, updateVendorInList, deleteVendorFromList } = useVendorStore();

  const getAllVendors = async ({ page = 1, pageSize = 10, orderBy, orderField, search, filterBy, status }: GetVendorsParams) => {
    setLoading(true);
    setError(null);

    try {
      const data = await getVendors({
        page,
        pageSize,
        orderBy,
        orderField,
        search,
        filterBy,
        token,
        status,
      });

      setVendorsPaginated(data);
      return { data, ok: true };
    } catch (err: any) {
      setError(err?.message || "Error fetching vendors");
      return { data: null, ok: false };
    } finally {
      setLoading(false);
    }
  };

  const getVendorByUid = useCallback(
    async (uid: string) => {
      setLoading(true);
      setError(null);

      try {
        const data = await getVendorByUidApi(uid);

        if (data) {
          updateVendorInList(data);
        }

        return { data, ok: true };
      } catch (err: any) {
        setError(err?.message || "Error fetching vendor");
        return { data: null, ok: false };
      } finally {
        setLoading(false);
      }
    },
    [updateVendorInList],
  );

  const createNewVendor = async (vendorData: any) => {
    setLoading(true);
    setError(null);

    try {
      const data = await createVendorApi(token, vendorData);
      const createdVendor = data?.data || data;
      if (createdVendor) {
        addVendor(createdVendor);
      }
      await invalidateContacts(queryClient);
      await invalidateContactResolutionQueries(queryClient);
      return { data: createdVendor, ok: true };
    } catch (err: any) {
      setError(err.message || "Error creating vendor");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const editVendor = async (id: string, vendorData: any) => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        vendorUid: id,
        ...vendorData,
      };

      const data = await updateVendorApi(token, payload);
      const updatedVendor = data?.data || data || { uid: id, ...vendorData };
      updateVendorInList(updatedVendor);
      await invalidateContacts(queryClient);
      await invalidateContactResolutionQueries(queryClient);
      return { data: updatedVendor, ok: true };
    } catch (err: any) {
      setError(err.message || "Error updating vendor");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeVendor = async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      await deleteVendorApi(token, id);
      deleteVendorFromList(id);
      await invalidateContacts(queryClient);
      await invalidateContactResolutionQueries(queryClient);
      showSnackbar("Vendor deleted successfully", "success");
    } catch (err: any) {
      setError(err.message || "Error deleting vendor");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    getAllVendors,
    getVendorByUid,
    createNewVendor,
    editVendor,
    removeVendor,
  };
};
